/**
 * Constantes de limites do sistema
 * Centralizadas para uso em validações, UI e mensagens de erro
 */

// ==========================================
// LIMITES DE CARACTERES
// ==========================================

export const LIMITS = {
  // Projetos
  PROJECT_NAME_MIN: 1,
  PROJECT_NAME_MAX: 100,
  PROJECT_DESCRIPTION_MAX: 500,
  PROJECT_CODE_MAX: 50,

  // Reuniões / Atas
  MEETING_NAME_MAX: 1000,
  MEETING_LOCATION_MAX: 1000,

  // Tópicos
  TOPIC_TITLE_MIN: 1,
  TOPIC_TITLE_MAX: 200,

  // Itens de Ata
  ITEM_DESCRIPTION_MIN: 1,
  ITEM_DESCRIPTION_MAX: 1_000_000,
  ITEM_ENTITY_MAX: 1000,

  // Desenvolvimento
  DEVELOPMENT_TEXT_MIN: 1,
  DEVELOPMENT_TEXT_MAX: 1_000_000,

  // Participantes
  PARTICIPANT_NAME_MIN: 1,
  PARTICIPANT_NAME_MAX: 1000,
  PARTICIPANT_COMPANY_MAX: 1000,
  PARTICIPANT_ROLE_MAX: 1000,
  PARTICIPANT_EMAIL_MAX: 1000,

  // Imagens
  IMAGE_MAX_SIZE_MB: 5,
  IMAGE_MAX_SIZE_BYTES: 5 * 1024 * 1024,
  IMAGE_DESCRIPTION_MAX: 1500,
  ATA_IMAGES_MAX: 20,

  // Compartilhamento
  SHARE_EMAILS_MAX: 10,

  // Upload
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const,

  // Auth
  EMAIL_MAX: 254,
  PASSWORD_MIN: 8,
  PASSWORD_MAX: 128,
  NAME_MIN: 3,
  NAME_MAX: 100,

  // Quantidade máxima
  MAX_TOPICS_PER_MEETING: 50,
  MAX_ITEMS_PER_TOPIC: 100,
  MAX_PARTICIPANTS_PER_MEETING: 100,
  MAX_DEVELOPMENTS_PER_ITEM: 50,
  MAX_PROJECTS: 100,
  MAX_MEETINGS_PER_PROJECT: 500,
} as const;

// ==========================================
// MENSAGENS DE ERRO DETALHADAS
// ==========================================

export const ERROR_MESSAGES = {
  // ---- Rede e Conexão ----
  NETWORK_ERROR: 'Sem conexão com a internet. Verifique sua rede Wi-Fi ou dados móveis e tente novamente.',
  TIMEOUT_ERROR: 'O servidor demorou muito para responder. Isso pode acontecer em horários de pico. Tente novamente em alguns segundos.',
  SERVER_ERROR: 'Ocorreu um erro interno no servidor. Nossa equipe foi notificada. Tente novamente em alguns minutos.',
  SERVICE_UNAVAILABLE: 'O serviço está temporariamente indisponível para manutenção. Tente novamente em alguns minutos.',

  // ---- Autenticação ----
  SESSION_EXPIRED: 'Sua sessão expirou por inatividade. Por favor, faça login novamente para continuar.',
  INVALID_CREDENTIALS: 'E-mail ou senha incorretos. Verifique seus dados e tente novamente.',
  EMAIL_NOT_CONFIRMED: 'Seu e-mail ainda não foi confirmado. Verifique sua caixa de entrada (incluindo spam) e clique no link de confirmação.',
  ACCOUNT_BLOCKED: 'Sua conta foi temporariamente bloqueada por excesso de tentativas. Aguarde alguns minutos e tente novamente.',
  PERMISSION_DENIED: 'Você não tem permissão para realizar esta ação. Entre em contato com o proprietário do projeto.',

  // ---- Validação de Campos ----
  FIELD_REQUIRED: (field: string) => `${field} é obrigatório. Por favor, preencha este campo.`,
  FIELD_TOO_SHORT: (field: string, min: number) => `${field} deve ter no mínimo ${min} caracteres. Adicione mais detalhes.`,
  FIELD_TOO_LONG: (field: string, max: number) => `${field} excedeu o limite de ${max} caracteres (${max} máx.). Reduza o texto para poder salvar.`,
  FIELD_CHAR_COUNT: (current: number, max: number) => `${current}/${max} caracteres`,
  FIELD_CHAR_COUNT_WARNING: (current: number, max: number) => `${current}/${max} caracteres — limite quase atingido!`,
  FIELD_CHAR_COUNT_EXCEEDED: (current: number, max: number) => `${current}/${max} caracteres — limite excedido! Reduza o texto.`,
  FIELD_INVALID_FORMAT: (field: string) => `${field} está em um formato inválido. Verifique e tente novamente.`,
  INVALID_DATE: 'Data inválida. Use o formato correto (ex: 01/01/2026).',
  INVALID_EMAIL: 'E-mail inválido. Verifique se digitou corretamente (ex: nome@empresa.com).',

  // ---- Limites de Quantidade ----
  MAX_TOPICS_REACHED: `Limite de ${LIMITS.MAX_TOPICS_PER_MEETING} tópicos por ata atingido. Remova tópicos não utilizados para adicionar novos.`,
  MAX_ITEMS_REACHED: `Limite de ${LIMITS.MAX_ITEMS_PER_TOPIC} itens por tópico atingido. Considere criar um novo tópico para organizar melhor.`,
  MAX_PARTICIPANTS_REACHED: `Limite de ${LIMITS.MAX_PARTICIPANTS_PER_MEETING} participantes por ata atingido. Remova participantes que não estão mais envolvidos.`,
  MAX_DEVELOPMENTS_REACHED: `Limite de ${LIMITS.MAX_DEVELOPMENTS_PER_ITEM} desenvolvimentos por item atingido.`,
  MAX_IMAGES_REACHED: `Limite de ${LIMITS.ATA_IMAGES_MAX} imagens por ata atingido. Remova imagens desnecessárias antes de adicionar novas.`,
  MAX_SHARE_EMAILS_REACHED: `Você pode enviar no máximo ${LIMITS.SHARE_EMAILS_MAX} convites por vez.`,
  MAX_PROJECTS_REACHED: `Limite de ${LIMITS.MAX_PROJECTS} projetos atingido. Exclua projetos antigos antes de criar novos.`,

  // ---- Salvamento ----
  SAVE_SUCCESS: 'Alterações salvas com sucesso.',
  SAVE_ERROR: 'Não foi possível salvar as alterações. Suas edições estão preservadas localmente. Tente salvar novamente.',
  SAVE_CONFLICT: 'Outro usuário modificou este registro. Recarregue a página para ver as alterações mais recentes.',
  AUTOSAVE_ERROR_NETWORK: 'Salvamento automático falhou — sem conexão com a internet. Seus dados estão seguros localmente e serão salvos quando a conexão voltar.',
  AUTOSAVE_ERROR_GENERIC: 'Salvamento automático falhou. Seus dados estão preservados localmente. Tente salvar manualmente.',

  // ---- Operações CRUD ----
  CREATE_SUCCESS: (entity: string) => `${entity} criado(a) com sucesso.`,
  UPDATE_SUCCESS: (entity: string) => `${entity} atualizado(a) com sucesso.`,
  DELETE_SUCCESS: (entity: string) => `${entity} excluído(a) com sucesso.`,
  CREATE_ERROR: (entity: string) => `Não foi possível criar ${entity}. Verifique os dados e tente novamente.`,
  UPDATE_ERROR: (entity: string) => `Não foi possível atualizar ${entity}. Verifique os dados e tente novamente.`,
  DELETE_ERROR: (entity: string) => `Não foi possível excluir ${entity}. Tente novamente.`,

  // ---- Upload ----
  UPLOAD_INVALID_TYPE: 'Tipo de arquivo não permitido. Use apenas JPG, PNG, WEBP ou GIF.',
  UPLOAD_TOO_LARGE: `Arquivo muito grande. O tamanho máximo é ${LIMITS.IMAGE_MAX_SIZE_MB}MB. Reduza o tamanho da imagem antes de enviar.`,
  UPLOAD_ERROR: 'Erro ao enviar arquivo. Verifique sua conexão e tente novamente.',
  UPLOAD_SUCCESS: 'Imagem enviada com sucesso.',

  // ---- Compartilhamento ----
  INVITE_SUCCESS: 'Convites enviados com sucesso! Os colaboradores receberão um e-mail.',
  INVITE_ERROR: 'Não foi possível enviar os convites. Verifique os e-mails e tente novamente.',
  INVITE_RESENT: 'Convite reenviado com sucesso.',
  INVITE_RESEND_ERROR: 'Não foi possível reenviar o convite. Tente novamente em alguns minutos.',
  PERMISSION_UPDATED: 'Permissão do colaborador atualizada.',
  PERMISSION_UPDATE_ERROR: 'Não foi possível atualizar a permissão. Tente novamente.',
  COLLABORATOR_REMOVED: 'Colaborador removido do projeto.',
  COLLABORATOR_REMOVE_ERROR: 'Não foi possível remover o colaborador. Tente novamente.',
  CANNOT_CHANGE_IMPLICIT: 'Não é possível alterar permissão do proprietário do projeto.',
  CANNOT_REMOVE_IMPLICIT: 'Não é possível remover o proprietário do projeto.',

  // ---- Ata específicos ----
  MEETING_FINALIZED: 'Esta ata já foi finalizada e não pode mais ser editada.',
  MEETING_NOT_FOUND: 'Ata não encontrada. Ela pode ter sido excluída ou você não tem acesso.',
  PROJECT_NOT_FOUND: 'Projeto não encontrado. Ele pode ter sido excluído ou você não tem acesso.',
  CLONE_SUCCESS: 'Ata clonada com sucesso! Uma cópia foi criada como rascunho.',
  CLONE_ERROR: 'Não foi possível clonar a ata. Tente novamente.',

  // ---- Genéricos ----
  UNKNOWN_ERROR: 'Ocorreu um erro inesperado. Por favor, tente novamente. Se o problema persistir, recarregue a página.',
  RESOURCE_NOT_FOUND: 'O recurso solicitado não foi encontrado. Ele pode ter sido movido ou excluído.',
  DUPLICATE_RESOURCE: 'Este registro já existe. Verifique se não há duplicatas.',
  RATE_LIMIT: 'Muitas requisições em pouco tempo. Aguarde alguns segundos e tente novamente.',
  DATA_LOAD_ERROR: 'Não foi possível carregar os dados. Verifique sua conexão e tente novamente.',
} as const;

// ==========================================
// HELPER: Verificar proximidade do limite
// ==========================================

export function getCharCountStatus(current: number, max: number): 'ok' | 'warning' | 'exceeded' {
  const ratio = current / max;
  if (ratio > 1) return 'exceeded';
  if (ratio >= 0.9) return 'warning';
  return 'ok';
}

export function getCharCountMessage(current: number, max: number): string {
  const status = getCharCountStatus(current, max);
  switch (status) {
    case 'exceeded':
      return ERROR_MESSAGES.FIELD_CHAR_COUNT_EXCEEDED(current, max);
    case 'warning':
      return ERROR_MESSAGES.FIELD_CHAR_COUNT_WARNING(current, max);
    default:
      return ERROR_MESSAGES.FIELD_CHAR_COUNT(current, max);
  }
}

export function getCharCountColor(current: number, max: number): string {
  const status = getCharCountStatus(current, max);
  switch (status) {
    case 'exceeded':
      return 'text-red-600 font-semibold';
    case 'warning':
      return 'text-amber-600 font-medium';
    default:
      return 'text-muted-foreground';
  }
}
